<template>
	<div class="multi-select">

		<input
			v-if="enableSearch"
			type="text"
			v-model="searchTerm"
			@keydown.enter="selectOption"
			class="search-input"
			placeholder="Search..."
		/>

		<ul class="selected-options-list" v-if="selectedItems.length > 0 && showSelectedList">
			<li
				v-for="option in selectedItems"
				:key="option"
				@click="removeItem(option)"
				class="option"
				:class="{ active: isSelected(option) }"
				>
				<div class="checkbox" />
				{{ getName(option) }}

				<template 
					v-if="showAsTags && showAsTags.length > 0"
					>
					<template 
						v-for="tag in showAsTags"
						>
						<span 
							v-if="getTag(option, tag)" 
							class="user-tag"
							:class="[
								'tag-' + tag
							]"
							>
							{{ getTag(option, tag) }}
						</span>
					</template>
				</template>
			</li>
		</ul>

		<ul 
			class="options-list"
			:class="enableSearch ? 'with-search' : ''"
			>
			<li
				v-for="option in filteredOptions"
				:key="option"
				@click="selectOption(option)"
				class="option"
				:class="{ 
					active: isSelected(option.id),
					'hold-select': holdSelection && holdSelection === option.id,
					'disabled': option.disabled
				}"
				>
				<div class="checkbox" />
				{{ option.name }}
				<span class="hold-selection-label" v-if="holdSelectionLabel && holdSelection === option.id">
					{{ holdSelectionLabel }}
				</span>

				<template 
					v-if="showAsTags && showAsTags.length > 0"
					>
					<template 
						v-for="tag in showAsTags"
						>
						<span 
							v-if="getTag(option.id, tag)" 
							class="user-tag"
							:class="[
								'tag-' + tag
							]"
							>
							{{ getTag(option.id, tag) }}
						</span>
					</template>
				</template>
			</li>
		</ul>

		<div
			v-if="showUpdateButton" 
			class="update-button"
			:class="{ disabled: !hasChanged }"
			@click="submitUpdate"
			>
			Update
		</div>

	</div>
</template>

<script>
export default {
	name: 'MultiSelect',
	props: {
		options: {
			type: Array,
			required: true,
		},
		modelValue: {
			type: [Array, String, Number],
			default: [],
			required: false
		},
		singleSelectionOnly: {
			type: Boolean,
			default: false
		},
		preselectAll: {
			type: Boolean,
			default: false
		},
		showSelectedList: {
			type: Boolean,
			default: true
		},
		enableSearch: {
			type: Boolean,
			default: true
		},
		holdSelection: {
			type: [Number, Boolean],
			default: false
		},
		holdSelectionLabel: {
			type: [String, Boolean],
			default: false
		},
		showUpdateButton: {
			type: Boolean,
			default: false
		},
		showAsTags: { // if it finds keys of these names then it will show them as tags next to the name. e.g. 'Case owner'
			type: [Boolean, Array],
			default: false
		}
	},
	emits: ['update:modelValue'],
	data() {
		return {
			selectedItems: this.modelValue,
			searchTerm: '',
			hasChanged: false
		};
	},
	mounted() {
		this.$nextTick(() => {
			this.selectedItems = this.modelValue
			if(this.singleSelectionOnly) {
				if(!Array.isArray(this.selectedItems)){
					this.selectedItems = [this.modelValue]
				}
			}

			// console.log('preselect', this.selectedItems)
			if(this.preselectAll && (!this.selectedItems || this.selectedItems.length === 0)){
				this.selectedItems = this.options.map(option => option.id)
				this.$emit('update:modelValue', this.selectedItems)
			}

			// console.log('preselect', this.selectedItems)
			// console.log('options', this.options)
		})
	},
	computed: {
		
		// filteredOptions shows all options available if no search term, and if there is a searchTerm then it will filter the options based on the searchTerm
		filteredOptions() {
			let output
			if (!this.searchTerm) {
				output = this.options
			}
			output = this.options.filter((option) =>
				option.name.toLowerCase().includes(this.searchTerm.toLowerCase())
			);

			// if showSelectedList is true, then filter out the selected items from the options list
			if(this.showSelectedList){
				output = output.filter(option => !this.selectedItems.includes(option.id))
			}

			return output
		}
	},
	methods: {
		// a method that returns true or false if the option is within selectedItems if its an array, and if selectedItems is a string or number then it will return true or false if the option is equal to selectedItems
		isSelected(optionID) {
			if (Array.isArray(this.selectedItems)) {
				return this.selectedItems.includes(optionID)
			}
			return this.selectedItems === optionID
		},
		selectOption(option) {
			this.hasChanged = true

			if(option.disabled){
				return
			}

			if (this.singleSelectionOnly) {
				if (!Array.isArray(this.selectedItems)) {
					this.selectedItems = []
				}

				if (!this.selectedItems.includes(option.id)) {
					this.selectedItems = []
					this.selectedItems.push(option.id)
					this.$emit('update:modelValue', this.selectedItems[0])
				}else{
					this.removeItem(option.id)
				}
			}else{
				if (!this.selectedItems.includes(option.id)) {
					this.selectedItems.push(option.id)
					this.$emit('update:modelValue', this.selectedItems)
				}else{
					this.removeItem(option.id)
				}
			}
			this.searchTerm = ''
		},
		removeItem(item) {
			this.hasChanged = true

			const index = this.selectedItems.indexOf(item)
			if (index !== -1) {
				this.selectedItems.splice(index, 1)
				if(this.singleSelectionOnly){
					this.$emit('update:modelValue', '')
				}else{
					this.$emit('update:modelValue', this.selectedItems)
				}
			}
		},
		getName(id) {
			let theOption = this.options.find(option => option.id === id)
			if(theOption){
				return theOption.name
			}else{
				return 'N/A'
			}
		},
		getTag(id, tag){
			let theOption = this.options.find(option => option.id === id)
			
			if(theOption && theOption[tag] && (theOption[tag] !== '' && theOption[tag] !== null && theOption[tag] !== undefined)){
				return theOption[tag]
			}else{
				return false
			}
		},
		submitUpdate(){
			this.hasChanged = false
			this.$emit('updateRequested')
		}
	},
};
</script>

<style lang="sass">

.multi-select
	position: relative
	display: flex
	flex-direction: column

	.selected-items
		display: flex
		flex-wrap: wrap
		align-items: center
		border: 1px solid #ccc
		padding: 5px
		min-height: 30px

	.selected-item
		display: flex
		align-items: center
		background-color: #f0f0f0
		border-radius: 3px
		padding: 2px 5px
		margin-right: 5px
		margin-bottom: 5px

	.remove-item
		cursor: pointer
		margin-left: 5px

	.update-button
		cursor: pointer
		padding: 5px
		background-color: #5AAE4B
		border-radius: 3px
		text-align: center
		margin-top: 5px
		color: #fff
		font-weight: 700
		transition: all 300ms

		&:hover
			background-color: #4A9C3F

		&.disabled
			user-select: none
			pointer-events: none
			opacity: 0.3
			cursor: not-allowed


	.search-input
		width: 100%
		padding: 5px
		border: 1px solid #ccc
		border-radius: 3px 3px 0 0

	.options-list, .selected-options-list
		width: 100%
		max-height: 280px
		overflow-y: auto
		background-color: #fff
		border: 1px solid #ccc
		border-radius: 3px
		padding: 5px 0
		margin: 0
		list-style: none

		&.with-search
			border-top: none
			border-radius: 0 0 3px 3px

		li
			padding: 5px 15px
			cursor: pointer
			display: flex
			align-items: center
			gap: 10px
			user-select: none

			.hold-selection-label
				font-size: 12px
				color: #d00

			.user-tag
				font-size: 10px
				color: #fff
				padding: 2px 5px
				border-radius: 3px
				background-color: #5c92a3
				line-height: 1
				font-weight: 700

				&.tag-notifications_enabled
					background-color: #d00

				&.tag-user_type
					background-color: #fc0
					color: #666

			.checkbox
				display: flex
				align-items: center
				justify-content: center
				width: 20px
				height: 20px
				border: 2px solid #111
				border-radius: 4px
				position: relative

			&.disabled
				opacity: 0.5
				pointer-events: none

				.checkbox
					overflow: hidden

					&:after, &:before
						content: ''
						display: block
						position: absolute
						width: 100%
						height: 2px
						background-color: #d00
						opacity: 0.8
					
					&:after
						transform: rotate(45deg)

					&:before
						transform: rotate(-45deg)


			&.hold-select
				background-color: #f0f0f0

				.checkbox
					&:after
						content: ''
						display: block
						position: absolute
						width: 10px
						height: 10px
						background-color: rgba(#009900, 0.4)
						border-radius: 2px

			&.active
				background-color: #f0f0f0

				.checkbox
					&:after
						content: ''
						display: block
						position: absolute
						width: 10px
						height: 10px
						background-color: #009900
						border-radius: 2px

	.selected-options-list
		border-bottom: 1px solid #777
		border-radius: 0

	.option
		padding: 5px
		cursor: pointer

		&:hover
			background-color: #f0f0f0

</style>